import { Box } from '@cdl/heartbeat-react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { squareToPixel } from '../../../plot/utils/graph.utils';
import { Cluster } from '../../clustering.types';
import { BaseClusterInformation } from '../baseClusters/BaseClusterInfo';
import { MorphologyActionBar } from './MorphologyActionBar';
import { CanvasStrip } from '../../../plot/contexts/canvas-strip.context';
import {
  clusterHorizontalLengthSelector,
  clusterVerticalRangeSelector,
  disableMergeSelector,
  selectedBaseClustersSelector,
} from '../../selectors/cluster.selectors';
import { gainSelector } from '../../../layout/selectors/layout.selectors';

export const MORPHOLOGY_WIDTH = squareToPixel(14);
export const MORPHOLOGY_HEIGHT = squareToPixel(5);
export const CONTAINER_WIDTH = MORPHOLOGY_WIDTH + 4;
export const CONTAINER_HEIGHT = MORPHOLOGY_HEIGHT + squareToPixel(1);

interface Props {
  morphology: Cluster;
}

export const Morphology = forwardRef<HTMLDivElement, Props>(
  ({ morphology }, ref) => {
    const MemoisedBaseMorphologyInformation = useMemo(
      () => <BaseClusterInformation cluster={morphology} />,
      [morphology],
    );
    const disabled = useSelector(disableMergeSelector);
    const [hovered, setHovered] = useState(false);
    const gain = useSelector(gainSelector);
    const xMax = useSelector(clusterHorizontalLengthSelector(morphology.id));
    const { yMin, yMax } = useSelector(clusterVerticalRangeSelector);
    const selectedClusters = useSelector(selectedBaseClustersSelector);
    const shadowBeats = useMemo(
      () => selectedClusters.flatMap((cluster) => cluster.beats),
      [selectedClusters],
    );
    const onMouseOver = useCallback(() => {
      setHovered(true);
    }, []);

    const onMouseLeave = useCallback(() => {
      setHovered(false);
    }, []);

    // TODO: add on the MorphologiesContainer another WebGLContainer
    // for the shadow beat and remove entirely the <CanvasStrip /> context from here
    return (
      <Box my='m' mr='m' height={CONTAINER_HEIGHT}>
        <Box
          height={MORPHOLOGY_HEIGHT}
          width={CONTAINER_WIDTH}
          ref={ref}
          bgColor='transparent'
          overflow='hidden'
          borderColor='neutral.100'
          border={'0.125rem solid'}
          borderRadius='0.25rem'
          position='relative'
          data-testid='morphology'
          onMouseLeave={onMouseLeave}
          onMouseOver={onMouseOver}
        >
          <CanvasStrip
            clusterId={morphology.id}
            beats={[]}
            width={MORPHOLOGY_WIDTH}
            height={MORPHOLOGY_HEIGHT}
            xAxis={{ xMax }}
            yAxis={{ yMin: yMin / gain, yMax: yMax / gain }}
            shadowBeats={hovered && !disabled ? shadowBeats : undefined}
            inverseBeats={true}
          >
            {MemoisedBaseMorphologyInformation}
          </CanvasStrip>
        </Box>
        <MorphologyActionBar morphology={morphology} />
      </Box>
    );
  },
);
